<template>
  <div class="GenerateInvoicePage">
    <h1>Generate Invoice</h1>
    <p class="GenerateInvoicePage-intro">
      Search for an organiser, then select them to generate an invoice for their payments in a date range.
    </p>

    <!-- Organiser search -->
    <div class="GenerateInvoicePage-search Card Card--top">
      <div class="Card-body">
        <form @submit.prevent="searchUsers" class="GenerateInvoicePage-searchForm">
          <div class="Form-inputGroup">
            <label class="Form-label" for="organiser-search">Search organisers (name or email)</label>
            <input
              id="organiser-search"
              v-model="searchQuery"
              type="text"
              class="Input-field"
              placeholder="Enter at least 2 characters"
              minlength="2"
            />
          </div>
          <button type="submit" class="Button" :disabled="searchQuery.length < 2 || searchLoading">
            {{ searchLoading ? 'Searching…' : 'Search' }}
          </button>
        </form>
        <p v-if="searchError" class="Form-error">{{ searchError }}</p>
        <div v-if="searchResults.length > 0" class="GenerateInvoicePage-results">
          <p class="GenerateInvoicePage-resultsTitle">Select an organiser:</p>
          <ul class="GenerateInvoicePage-resultsList">
            <li
              v-for="u in searchResults"
              :key="u.id"
              class="GenerateInvoicePage-resultItem"
              :class="{ 'GenerateInvoicePage-resultItem--selected': selectedUser && selectedUser.id === u.id }"
            >
              <button
                type="button"
                class="GenerateInvoicePage-resultButton"
                @click="selectUser(u)"
              >
                <strong>{{ u.forename }} {{ u.surname }}</strong>
                <span v-if="u.email" class="GenerateInvoicePage-resultEmail">{{ u.email }}</span>
                <span v-if="u.company" class="GenerateInvoicePage-resultCompany">{{ u.company }}</span>
              </button>
            </li>
          </ul>
        </div>
        <p v-else-if="searchDone && searchQuery.length >= 2" class="GenerateInvoicePage-noResults">
          No organisers found. Try a different search.
        </p>
      </div>
    </div>

    <!-- Generate invoice form (when user selected) -->
    <div v-if="selectedUser && selectedUser.id" class="GenerateInvoicePage-form">
      <generate-invoice
        :user="selectedUser"
        @close="clearSelection"
        @success="onInvoiceSuccess"
      />
    </div>
    <p v-else-if="!selectedUser || !selectedUser.id" class="GenerateInvoicePage-hint">
      Search and select an organiser above to generate an invoice.
    </p>
  </div>
</template>

<script>
import axios from 'axios';
import cookieModel from '../../models/cookie';
import errorModel from '../../models/error';
import GenerateInvoice from '../../components/generate-invoice/generate-invoice.vue';

export default {
  name: 'GenerateInvoicePage',
  components: {
    GenerateInvoice
  },
  data() {
    return {
      baseUrl: process.env.VUE_APP_API_URL,
      axiosInstance: axios.create({ baseURL: this.baseUrl }),
      searchQuery: '',
      searchLoading: false,
      searchDone: false,
      searchError: '',
      searchResults: [],
      selectedUser: {
        id: null,
        forename: '',
        surname: '',
        email: '',
        company: ''
      }
    };
  },
  methods: {
    searchUsers() {
      const vm = this;
      if (vm.searchQuery.length < 2) {
        vm.searchError = 'Please enter at least 2 characters';
        return;
      }
      vm.searchError = '';
      vm.searchLoading = true;
      vm.searchDone = false;
      vm.axiosInstance.defaults.headers.common['Authorization'] =
        'Bearer ' + cookieModel.getCookie('ticketlab_access');

      vm.axiosInstance
        .post(vm.baseUrl + '/einstein/user/search', { search: vm.searchQuery })
        .then((response) => {
          vm.searchLoading = false;
          vm.searchDone = true;
          if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
            vm.searchResults = response.data.data.map((row) => ({
              id: row.id,
              forename: row.forename || '',
              surname: row.surname || '',
              email: row.email || '',
              company: row.company || ''
            }));
          } else {
            vm.searchResults = [];
          }
        })
        .catch((error) => {
          vm.searchLoading = false;
          vm.searchDone = true;
          vm.searchResults = [];
          vm.searchError = errorModel.apiError(error, vm.$router) || 'Search failed. Please try again.';
        });
    },
    selectUser(user) {
      this.selectedUser = {
        id: user.id,
        forename: user.forename || '',
        surname: user.surname || '',
        email: user.email || '',
        company: user.company || ''
      };
    },
    clearSelection() {
      this.selectedUser = {
        id: null,
        forename: '',
        surname: '',
        email: '',
        company: ''
      };
    },
    onInvoiceSuccess() {
      // Optional: refresh or show global success; form already shows success state
    }
  }
};
</script>

<style lang="scss" scoped>
.GenerateInvoicePage {
  padding: 0 1rem;
}

.GenerateInvoicePage-intro {
  margin-bottom: 1.5rem;
  color: #666;
}

.GenerateInvoicePage-searchForm {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.GenerateInvoicePage-results {
  margin-top: 1rem;
}

.GenerateInvoicePage-resultsTitle {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.GenerateInvoicePage-resultsList {
  list-style: none;
  padding: 0;
  margin: 0;
}

.GenerateInvoicePage-resultItem {
  margin-bottom: 0.25rem;
}

.GenerateInvoicePage-resultButton {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 1rem;
}

.GenerateInvoicePage-resultButton:hover {
  background: #f5f5f5;
  border-color: #ccc;
}

.GenerateInvoicePage-resultItem--selected .GenerateInvoicePage-resultButton {
  border-color: #4caf50;
  background: #e8f5e9;
}

.GenerateInvoicePage-resultEmail,
.GenerateInvoicePage-resultCompany {
  display: block;
  font-size: 0.9rem;
  color: #666;
  font-weight: normal;
}

.GenerateInvoicePage-noResults {
  margin-top: 1rem;
  color: #666;
}

.GenerateInvoicePage-form {
  margin-top: 1.5rem;
}

.GenerateInvoicePage-hint {
  margin-top: 1rem;
  color: #999;
  font-style: italic;
}
</style>
